import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  getNotificationsStore,
  notificationHref,
  useLiveNotifications,
  type AppNotification,
} from "@weco/suite-chrome";
import { showOsNotification, tabIsHidden } from "./osNotification";

/**
 * Whether a live notification is about the screen the user is looking at —
 * then it says nothing they cannot already see.
 */
export function isAboutCurrentScreen(n: AppNotification, pathname: string): boolean {
  if (n.category !== "chat" && n.category !== "email") return false;
  const href = notificationHref(n).split("?")[0];
  // A bare module home is not "this conversation".
  return href !== "/chat" && href !== "/email" && pathname === href;
}

/**
 * Surfaces live notifications outside the bell: a toast while the tab is
 * visible, the browser's own banner while it is hidden (if permitted).
 * Incoming calls are left to the call banner, which rings; only a hidden tab
 * gets an OS banner for them. Mounted once in the authenticated shell.
 */
export function NotificationsBridge({ enabled = true }: { enabled?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const open = useCallback(
    (n: AppNotification) => {
      getNotificationsStore().markRead(n.id);
      navigate(notificationHref(n));
    },
    [navigate],
  );

  useLiveNotifications(
    (n) => {
      // The server decides whether this may interrupt (preferences, DND,
      // quiet hours); the bell lists it either way.
      if (tabIsHidden()) {
        if (n.alert?.push === false) return;
        showOsNotification({
          title: n.title,
          body: n.body,
          tag: n.group_key ?? n.id,
          requireInteraction: n.kind === "call.incoming",
          onClick: n.kind === "call.incoming" ? undefined : () => open(n),
        });
        return;
      }
      if (n.kind === "call.incoming") return;
      if (n.alert?.in_app === false) return;
      if (isAboutCurrentScreen(n, location.pathname)) return;
      toast(n.title, {
        id: `notification-${n.id}`,
        description: n.body || undefined,
        action: { label: t("notifications.open"), onClick: () => open(n) },
      });
    },
    enabled,
  );

  return null;
}
